import React from 'react';
import { Navigate } from 'react-router-dom';

/**
 * The single definition of what an admin tab is and which Resource governs
 * it -- shared by AdminLayout (the tab strip), App.js (the /admin index
 * redirect and the RequirePermission wrapper on every /admin/* route) and
 * Sidebar (whether the Admin nav link exists at all). Three consumers need
 * the same answer, so it lives in one place rather than three.
 *
 * `resource` is a models.Resource value except for document-type-rubrics,
 * which has no Resource of its own -- GET/PUT /api/document-types/{id}/rubric
 * are guarded on document_types.read/update, so it maps there too.
 */
export const ADMIN_TABS = [
  { to: '/admin/hierarchy', label: '🏢 Hierarchy', resource: 'hierarchy' },
  { to: '/admin/workflows', label: '🔀 Workflows', resource: 'workflows' },
  { to: '/admin/change-categories', label: '↩️ Change Categories', resource: 'change_categories' },
  { to: '/admin/regulatory-references', label: '📖 Regulatory References', resource: 'regulatory_references' },
  { to: '/admin/regulatory-categories', label: '🗂️ Regulatory Categories', resource: 'regulatory_categories' },
  { to: '/admin/review-frequencies', label: '📅 Review Frequencies', resource: 'review_frequencies' },
  { to: '/admin/policy-areas', label: '📁 Policy Areas', resource: 'policy_areas' },
  { to: '/admin/applicability-groups', label: '🎯 Applicability Groups', resource: 'applicability_groups' },
  { to: '/admin/document-templates', label: '📄 Document Templates', resource: 'document_templates' },
  { to: '/admin/document-types', label: '🏷️ Document Types', resource: 'document_types' },
  // No Resource of its own -- rubric endpoints are guarded on document_types.
  { to: '/admin/document-type-rubrics', label: '📐 Document Type Rubrics', resource: 'document_types' },
  // No Resource of its own -- the source endpoints are guarded on
  // regulatory_references, since a loaded source is a property of a reference.
  { to: '/admin/regulatory-sources', label: '📚 Source Library', resource: 'regulatory_references' },
  { to: '/admin/analytics', label: '📈 Analytics', resource: 'analytics' },
  { to: '/admin/acknowledgements', label: '✍️ Acknowledgements', resource: 'acknowledgements' },
  { to: '/admin/users', label: '👥 Users', resource: 'users' },
  { to: '/admin/settings', label: '⚙️ System Settings', resource: 'settings' },
];

/**
 * A tab is visible when the viewer holds ANY action (read/create/update/
 * delete) on its resource -- not `read` alone. hierarchy.read and
 * workflows.read are enforced NOWHERE in the backend (every workflows GET is
 * open, and DocumentForm's node picker fetches /api/hierarchy/nodes for
 * every document author regardless of permission -- the issue-#22
 * /selectable trap), so a read-only rule could never hide those two tabs. A
 * uniform "any action" rule needs no per-tab exception list.
 */
export function visibleAdminTabs(can) {
  return ADMIN_TABS.filter((tab) => (
    can(tab.resource, 'read')
    || can(tab.resource, 'create')
    || can(tab.resource, 'update')
    || can(tab.resource, 'delete')
  ));
}

/**
 * The /admin index route. Lands on the first tab the viewer can see rather
 * than a hardcoded "hierarchy" -- hierarchy is the tab most users will lose
 * under permission gating, so hardcoding it would be the worst possible
 * landing page. When nothing is visible, render an explanation rather than
 * redirect: a redirect to "/" plus App.js's "*" catch-all (which also goes
 * to "/") is how you build a loop.
 */
export function AdminIndexRedirect({ can }) {
  const tabs = visibleAdminTabs(can);
  if (tabs.length === 0) {
    return (
      <div className="card">
        <div className="empty-state">
          <h3>No administration access</h3>
          <p>You don't have permission to manage any administrative area.</p>
        </div>
      </div>
    );
  }
  return <Navigate to={tabs[0].to} replace />;
}
